/**
 * The credit note's printable form — `./Print.tsx`'s shape for the credit note (CLAUDE.md "Print
 * routes: chrome-free by routing"). A top-level sibling of "/" in `main.tsx`, never a child of
 * RootRoute, so no sidebar, header or navigation landmark ever mounts around a statutory document;
 * and because it inherits no root loader, it does its own account/permission read (`billing.read`,
 * the same permission `GET /admin/credit-notes/{id}` requires).
 *
 * Reads the same `getCreditNote` the on-screen detail reads and hands it to `CreditNoteDocument`
 * (`@steelyard/theme`), so the printed and on-screen figures come from one read of the API.
 */
import { Link, redirect, useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { ApiError } from "../../lib/api";
import { getAccount, getPermissionState, safeReturnTo } from "../../lib/identity";
import { can } from "../../lib/permissions";
import { AmbiguousNumberError, IRN_STATE, creditNoteByKey, type CreditNoteDetail } from "../../lib/services/invoicing";
import { AmbiguousNumber } from "./number-route-error";
import { creditNotePath } from "../../lib/paths";
import { stateName } from "../../lib/invoice-model";
import { CreditNoteDocument, ErrorSurface } from "@steelyard/theme";
import { Button } from "rizzui";
import { PrintNow } from "./Print";
import "../../print.css";

type PrintLoaderData =
  | { kind: "forbidden" }
  | { kind: "error"; href: string; error: unknown }
  | { kind: "document"; href: string; data: CreditNoteDetail };

export async function printCreditNoteLoader({ params, request }: LoaderFunctionArgs): Promise<PrintLoaderData> {
  const key = params.creditNoteNumber!;
  const path = new URL(request.url).pathname;

  const account = await getAccount();
  if (!account) throw redirect(`/sign-in?returnTo=${encodeURIComponent(safeReturnTo(path))}`);

  const permissionState = await getPermissionState();
  if (!can(permissionState, "billing.read")) return { kind: "forbidden" };

  try {
    // A legacy id throws a redirect to the number URL (not an ApiError — rethrown below), unless
    // its number is ambiguous; then this id URL is the record's address, so Back keeps it.
    const data = await creditNoteByKey(request, key);
    return { kind: "document", href: creditNotePath(key), data };
  } catch (error) {
    if (error instanceof ApiError || error instanceof AmbiguousNumberError) return { kind: "error", href: creditNotePath(key), error };
    throw error;
  }
}

const BACK_TO_NOTE = (href: string) => (
  <Link to={href}>
    <Button as="span" variant="outline" size="sm">
      Back to the credit note
    </Button>
  </Link>
);

export default function PrintCreditNotePage() {
  const data = useLoaderData() as PrintLoaderData;

  if (data.kind === "document") {
    return (
      <CreditNoteDocument data={{ ...data.data, placeOfSupply: stateName(data.data.placeOfSupply) }} irnState={IRN_STATE[data.data.irnStatus]}>
        <PrintNow />
        {BACK_TO_NOTE(data.href)}
      </CreditNoteDocument>
    );
  }

  if (data.kind === "error" && data.error instanceof AmbiguousNumberError) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <AmbiguousNumber error={data.error} />
      </div>
    );
  }

  const error =
    data.kind === "forbidden"
      ? { title: "Not permitted", status: 403, detail: "Printing a credit note is gated by billing.read." }
      : data.error;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <ErrorSurface error={error} action="print this credit note">
        {data.kind === "forbidden" ? null : BACK_TO_NOTE(data.href)}
      </ErrorSurface>
    </div>
  );
}
